import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  TextField,
  Divider,
  Paper,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchHotelById } from '../../services/hotelService';
import { createBooking } from '../../services/bookingService';
import SEO from '../../components/SEO';
import { PageHero, Stepper as UiStepper } from '../../components/ui';

// Helper function to add days to a date
const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// Helper function to get difference in days between two dates
const differenceInDays = (date2, date1) => {
  const diffTime = Math.abs(date2 - date1);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Helper function to format date to YYYY-MM-DD
const formatDate = (date) => {
  const d = new Date(date);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [year, month, day].join('-');
};



// Booking steps
const steps = ['Room Selection', 'Guest Details', 'Confirm Booking'];

const HotelBooking = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [bookingData, setBookingData] = useState({
    roomTypeId: '',
    checkInDate: formatDate(new Date()),
    checkOutDate: formatDate(addDays(new Date(), 2)),
    adults: 2,
    children: 0,
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: '',
    specialRequests: ''
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [apiHotel, setApiHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadHotel = async () => {
      try {
        setLoading(true);
        const data = await fetchHotelById(id);
        setApiHotel(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching hotel details:', err);
        setError(t('Failed to load hotel details. Please try again later.'));
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadHotel();
    }
  }, [id, t]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !apiHotel) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <Typography variant="h5" component="h1" color="error" gutterBottom>
          {error || t('Hotel not found')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/hotels')}
          sx={{ mt: 2 }}
        >
          {t('Back to Hotels')}
        </Button>
      </Container>
    );
  }

  const hotel = {
    ...apiHotel,
    name: apiHotel.name,
    roomTypes: (apiHotel.roomTypes || []).map(rt => ({
      id: rt.id,
      type: rt.name,
      price: rt.basePrice || 0,
      capacity: `${rt.maxAdults} Adults${rt.maxChildren ? `, ${rt.maxChildren} Child` : ''}`
    }))
  };

  // Calculate total price
  const selectedRoom = hotel.roomTypes.find(room => room.id === bookingData.roomTypeId);
  const checkInDate = new Date(bookingData.checkInDate);
  const checkOutDate = new Date(bookingData.checkOutDate);
  const nights = differenceInDays(checkOutDate, checkInDate);
  const totalPrice = selectedRoom ? selectedRoom.price * Math.max(1, nights) : 0;

  // Handle form changes
  const handleChange = (field) => (event) => {
    setBookingData({
      ...bookingData,
      [field]: event.target.value
    });
  };

  // Handle step navigation
  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      try {
        setSubmitting(true);
        const selectedRoom = hotel.roomTypes.find(room => room.id === bookingData.roomTypeId);

        await createBooking({
          type: 'hotel',
          hotelId: hotel.id,
          roomTypeId: bookingData.roomTypeId,
          roomType: selectedRoom?.type || 'Standard',
          checkInDate: bookingData.checkInDate,
          checkOutDate: bookingData.checkOutDate,
          numberOfGuests: Number(bookingData.adults) + Number(bookingData.children),
          specialRequests: bookingData.specialRequests,
          totalAmount: totalPrice
        });

        setSnackbarOpen(true);
        setTimeout(() => {
          navigate(`/hotels/${id}`);
        }, 3000);
      } catch (err) {
        console.error('Error creating booking:', err.response?.data || err);
        setError(JSON.stringify(err.response?.data?.message || err.message));
      } finally {
        setSubmitting(false);
      }
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Render different form steps
  const renderStepContent = (step) => {
    switch (step) {
      case 0: // Room Selection
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                SelectProps={{ native: true }}
                label={t('Room Type')}
                value={bookingData.roomTypeId}
                onChange={handleChange('roomTypeId')}
                fullWidth
                required
                margin="normal"
              >
                <option value="" disabled>
                  {t('Select a room type')}
                </option>
                {hotel.roomTypes.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.type} - ₹{room.price} ({room.capacity})
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                label={t('Check-in Date')}
                value={bookingData.checkInDate}
                onChange={handleChange('checkInDate')}
                fullWidth
                required
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                label={t('Check-out Date')}
                value={bookingData.checkOutDate}
                onChange={handleChange('checkOutDate')}
                fullWidth
                required
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="number"
                label={t('Adults')}
                value={bookingData.adults}
                onChange={handleChange('adults')}
                fullWidth
                InputProps={{ inputProps: { min: 1, max: 10 } }}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="number"
                label={t('Children')}
                value={bookingData.children}
                onChange={handleChange('children')}
                fullWidth
                InputProps={{ inputProps: { min: 0, max: 6 } }}
                margin="normal"
              />
            </Grid>
          </Grid>
        );

      case 1: // Guest Details
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('First Name')}
                value={bookingData.firstName}
                onChange={handleChange('firstName')}
                fullWidth
                required
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label={t('Last Name')}
                value={bookingData.lastName}
                onChange={handleChange('lastName')}
                fullWidth
                required
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label={t('Email')}
                type="email"
                value={bookingData.email}
                onChange={handleChange('email')}
                fullWidth
                required
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label={t('Phone')}
                value={bookingData.phone}
                onChange={handleChange('phone')}
                fullWidth
                required
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t('Special Requests')}
                value={bookingData.specialRequests}
                onChange={handleChange('specialRequests')}
                fullWidth
                multiline
                rows={4}
                margin="normal"
              />
            </Grid>
          </Grid>
        );

      case 2: // Confirm Booking
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>{t('Booking Summary')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}><Typography color="text.secondary">{t('Hotel')}</Typography></Grid>
                  <Grid item xs={6}><Typography fontWeight={600}>{hotel.name}</Typography></Grid>

                  {selectedRoom && (
                    <>
                      <Grid item xs={6}><Typography color="text.secondary">{t('Room Type')}</Typography></Grid>
                      <Grid item xs={6}><Typography fontWeight={600}>{selectedRoom.type}</Typography></Grid>
                    </>
                  )}

                  <Grid item xs={6}><Typography color="text.secondary">{t('Check-in')}</Typography></Grid>
                  <Grid item xs={6}><Typography>{bookingData.checkInDate}</Typography></Grid>

                  <Grid item xs={6}><Typography color="text.secondary">{t('Check-out')}</Typography></Grid>
                  <Grid item xs={6}><Typography>{bookingData.checkOutDate}</Typography></Grid>

                  <Grid item xs={6}><Typography color="text.secondary">{t('Guests')}</Typography></Grid>
                  <Grid item xs={6}><Typography>{Number(bookingData.adults) + Number(bookingData.children)} ({bookingData.adults} {t('adults')}{Number(bookingData.children) > 0 ? `, ${bookingData.children} ${t('children')}` : ''})</Typography></Grid>

                  <Grid item xs={6}><Typography color="text.secondary">{t('Guest Name')}</Typography></Grid>
                  <Grid item xs={6}><Typography>{bookingData.firstName} {bookingData.lastName}</Typography></Grid>

                  <Grid item xs={6}><Typography color="text.secondary">{t('Nights')}</Typography></Grid>
                  <Grid item xs={6}><Typography>{nights}</Typography></Grid>

                  <Grid item xs={12}><Divider /></Grid>

                  <Grid item xs={6}><Typography variant="h6">{t('Total Amount')}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="h5" color="primary" fontWeight={700}>₹{totalPrice.toLocaleString()}</Typography></Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  // Check if the current step is valid to proceed
  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return !!bookingData.roomTypeId;
      case 1:
        return !!bookingData.firstName && !!bookingData.lastName && !!bookingData.email && !!bookingData.phone;
      case 2:
        return true; // In a real app, you would validate payment details
      default:
        return false;
    }
  };

  return (
    <>
      <SEO title={`${t('Book your stay')} — ${hotel.name}`} canonicalUrl={`/hotels/${id}/booking`} noindex />
      <PageHero
        compact
        crumbs={[{ label: t('Home'), to: '/' }, { label: t('Stay & Accommodation'), to: '/hotels' }, { label: hotel.name, to: `/hotels/${id}` }, { label: t('Booking') }]}
        title={t('Book your stay')}
        subtitle={hotel.name}
      />
      <div className="container booking-wrap">
      <Paper className="card booking__panel" sx={{ p: 3, mb: 4 }}>
        <UiStepper steps={steps.map((label) => t(label))} current={activeStep} />

        <Box sx={{ mb: 4 }}>
          {renderStepContent(activeStep)}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {activeStep > 0 && (
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                {t('Back')}
              </Button>
            )}
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            {selectedRoom && (
              <Typography variant="body1" sx={{ mr: 2 }}>
                {t('Total')}: <strong>₹{totalPrice}</strong> ({nights} {nights === 1 ? t('night') : t('nights')})
              </Typography>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={!isStepValid() || submitting}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : (activeStep === steps.length - 1 ? t('Complete Booking') : t('Next'))}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {t('Booking successful! Thank you for choosing')} {hotel.name}.
        </Alert>
      </Snackbar>
      </div>
    </>
  );
};

export default HotelBooking;
